import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { GraduationCap, Play, ArrowLeft, Sparkles } from "lucide-react";
import api from "@/lib/api";
import GameCanvas from "@/components/GameCanvas";
import Logo from "@/components/Logo";
import { Spinner } from "@/components/Bits";
import { Button } from "@/components/ui/button";

export default function ClassShowcase() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    api.get(`/school/public/${code}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [code]);

  if (error) return (
    <div className="min-h-screen grid place-items-center bg-[#05060a] text-white text-center p-6">
      <div><div className="text-5xl mb-3">🏫</div><h1 className="font-display text-2xl font-bold">Class not found</h1>
      <Button onClick={() => navigate("/")} className="mt-4 rounded-full">Go home</Button></div>
    </div>
  );
  if (!data) return <div className="min-h-screen bg-[#05060a]"><Spinner /></div>;

  return (
    <div className="min-h-screen bg-[#05060a] text-white">
      <header className="border-b border-white/5 px-5 h-16 flex items-center justify-between">
        <Logo to="/" size="md" />
        <Button variant="outline" className="rounded-full border-white/20 bg-white/5" onClick={() => navigate("/join?code=" + data.code)} data-testid="showcase-join-btn">
          <GraduationCap className="w-4 h-4" /> Join this class
        </Button>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-10">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-semibold text-[#9cc7ff] mb-4">
          <Sparkles className="w-3.5 h-3.5" /> Class Showcase
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold">{data.name}</h1>
        <p className="text-white/60 mt-2">by {data.teacher_name} · {data.games.length} game{data.games.length !== 1 ? "s" : ""}</p>
        {data.description && <p className="text-white/50 mt-1 max-w-2xl">{data.description}</p>}

        {playing && (
          <div className="mt-8 max-w-3xl mx-auto">
            <Button variant="ghost" size="sm" className="mb-3" onClick={() => setPlaying(null)}><ArrowLeft className="w-4 h-4" /> Back to games</Button>
            <h2 className="font-display text-xl font-bold mb-3">{playing.title}</h2>
            <GameCanvas config={playing.config} />
          </div>
        )}

        {!playing && (
          data.games.length === 0 ? (
            <div className="mt-10 text-center text-white/50">No published games yet — check back soon!</div>
          ) : (
            <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {data.games.map((g) => (
                <button key={g.id} onClick={() => setPlaying(g)} data-testid={`showcase-game-${g.id}`}
                  className="text-left rounded-2xl border border-white/10 bg-[#0e1117] overflow-hidden card-hover group">
                  <div className="relative h-32 grid place-items-center" style={{ background: `linear-gradient(135deg, ${g.world?.bgColor || "#0b1026"}, ${g.world?.accent || "#1f6feb"})` }}>
                    {g.cover_image ? <img src={`${process.env.REACT_APP_BACKEND_URL}${g.cover_image}`} alt="" className="absolute inset-0 w-full h-full object-cover" /> : <span className="text-5xl">{g.cover_emoji}</span>}
                    <div className="absolute inset-0 grid place-items-center bg-black/0 group-hover:bg-black/30 transition-colors"><Play className="w-10 h-10 opacity-0 group-hover:opacity-100 transition-opacity" fill="white" /></div>
                  </div>
                  <div className="p-3"><div className="font-bold truncate">{g.title}</div><div className="text-xs text-white/50 truncate">by {g.owner_name}</div></div>
                </button>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}
